import React from 'react';
import { BadgeCheck, ChevronRight, LogOut, Brain, Flower2, Apple, Sparkles, Stethoscope, Users, Download, ShieldCheck } from 'lucide-react';
import { isTestUserSession } from '../utils/testProfessional';
import QuickBooking from './QuickBooking';

import LoggedInDashboard from './LoggedInDashboard';
import ScheduleSection from './ScheduleSection';
import florLogo from '../assets/florDaisiesSF.png';
import letrasLogo from '../assets/daisies_letras.png';
import bannerImg from "../assets/bannerimg.png";
import PricingSection from './PricingSection';
import TopBanner from './TopBanner';

const SPECIALTY_PILLS = [
  { id: 16, name: 'Psicología', Icon: Brain },
  { id: 15, name: 'Ginecología', Icon: Flower2 },
  { id: 17, name: 'Nutrición', Icon: Apple },
  { id: 19, name: 'Dermatología', Icon: Sparkles },
  { id: 18, name: 'Medicina General', Icon: Stethoscope },
];

const VALUE_PROPS = [
  {
    title: 'Todas tus especialidades',
    body: 'Ginecología, psicología, nutrición, dermatología y salud general, en un solo lugar.',
  },
  {
    title: 'Agenda en 2 minutos',
    body: 'Eliges un horario real y apartas tu consulta sin llamadas ni esperas.',
  },
  {
    title: 'Tus datos, protegidos',
    body: 'Tu expediente y tu información viajan cifrados, siempre.',
  },
];

const HeroSection = ({ onStartBooking, onSelectMembership, loggedInUser, reservation, consultations, onLogout, onLoginClick, onStartVerification, onRouteStartBooking, onOpenProfile }) => {
  return (
    <>

      <TopBanner />
      <div style={{ height: '40px' }} />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="flex justify-between items-center mb-8">
          {loggedInUser ? (
            /* Sesión iniciada: flor + saludo (clickeable → /perfil) */
            <button
              type="button"
              onClick={onOpenProfile}
              aria-label="Ver mi perfil"
              className="press-scale flex items-center gap-3 min-w-0 rounded-full -ml-1.5 pl-1.5 pr-3 py-1 text-left transition-colors hover:bg-brand-tint"
            >
              <img src={florLogo} alt="Daisies" className="h-11 w-auto shrink-0" />
              <div className="min-w-0">
                {/* Sesión dais_test/dais_intern: marcarlo en la línea superior
                    para no confundir cuentas de QA con reales; el nombre real
                    se mantiene visible abajo. */}
                <p className="text-brand-deep/60 text-[12px] font-semibold leading-none">
                  {isTestUserSession() ? 'Hola de nuevo, usuario de pruebas' : 'Hola de nuevo,'}
                </p>
                <p className="text-brand-deep font-bold text-[clamp(1.05rem,2.2vw,1.35rem)] leading-tight tracking-[-0.015em] truncate">
                  {(() => {
                    const apellido = (loggedInUser.apellido || '').trim();
                    const hasApellido = apellido && !/^sin\s+apellido$/i.test(apellido);
                    if (loggedInUser.nombre) {
                      return hasApellido ? `${loggedInUser.nombre} ${apellido}` : loggedInUser.nombre;
                    }
                    return loggedInUser.email;
                  })()}
                </p>
              </div>
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <img src={florLogo} alt="Daisies" className="h-12 w-auto" />
              <img src={letrasLogo} alt="Daisies" className="h-8 w-auto" />
            </div>
          )}
          {loggedInUser ? (
            <button
              onClick={onLogout}
              aria-label="Cerrar sesión"
              className="press-scale inline-flex items-center gap-2 text-brand border-[1.5px] border-brand rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-brand-tint transition-colors shrink-0"
            >
              <LogOut size={16} aria-hidden="true" />
              <span className="hidden md:inline">Cerrar sesión</span>
            </button>
          ) : (
            <button
              onClick={onLoginClick}
              className="press-scale text-brand border-[1.5px] border-brand rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-brand-tint transition-colors"
            >
              Iniciar sesión
            </button>
          )}
        </header>

        {/* Schedule browser — only for guests (not logged in) */}
        {!loggedInUser && (
          <ScheduleSection onStartBooking={onRouteStartBooking || onStartBooking} />
        )}

        {loggedInUser ? (
          /* Logged-in view: full dashboard */
          <LoggedInDashboard
            user={loggedInUser}
            reservation={reservation}
            consultations={consultations}
            onStartBooking={onStartBooking}
            onRouteStartBooking={onRouteStartBooking}
            onLogout={onLogout}
            onStartVerification={onStartVerification}
            onSelectMembership={onSelectMembership}
          />
        ) : (
          /* Default guest view */
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-bold text-brand-deep mb-6 text-[clamp(2.2rem,5.4vw,3.4rem)] leading-[1.04] tracking-[-0.03em] text-balance">
                Tu salud,<br />
                <span className="text-brand">en tus manos.</span>
              </h1>
              <p className="text-[clamp(1rem,1.4vw,1.15rem)] text-text-body mb-8 leading-[1.55] max-w-md">
                Consultas con doctoras certificadas desde la comodidad de tu hogar.
                Eliges especialidad, ves horarios reales y apartas tu lugar en minutos.
              </p>
              <button
                onClick={() => onStartBooking()}
                className="press-scale group inline-flex items-center gap-2 bg-brand text-white rounded-full px-7 py-3.5 text-[15px] font-semibold hover:bg-brand-dark transition-colors shadow-brand"
              >
                Agenda tu consulta de valoración
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </button>

              {/* Specialty quick-select pills */}
              <div className="flex flex-wrap gap-2 mt-6">
                {SPECIALTY_PILLS.map(({ id, name, Icon }) => (
                  <button
                    key={id}
                    onClick={() => onStartBooking(id)}
                    className="press-scale inline-flex items-center gap-2 bg-brand-tint text-brand-deep rounded-full px-4 py-2 text-[13px] font-semibold hover:bg-brand hover:text-white transition-colors"
                  >
                    <Icon size={14} aria-hidden="true" />
                    {name}
                  </button>
                ))}
              </div>

              {/* Trust strip inline (reemplaza hero-metric template) */}
              <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-10 text-[13px] text-brand-deep/70">
                <li className="inline-flex items-center gap-1.5">
                  <Users size={14} className="text-brand" aria-hidden="true" />
                  <span><strong className="font-bold text-brand-deep">+40</strong> doctoras</span>
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Stethoscope size={14} className="text-brand" aria-hidden="true" />
                  <span><strong className="font-bold text-brand-deep">+5,000</strong> consultas</span>
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Download size={14} className="text-brand" aria-hidden="true" />
                  <span><strong className="font-bold text-brand-deep">+65,000</strong> descargas</span>
                </li>
              </ul>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-card-lg overflow-hidden shadow-elevated bg-brand-tint">
                <img
                  src={bannerImg}
                  alt="Doctoras de Daisies en consulta"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -right-3 sm:-right-5 bg-surface-card p-4 rounded-card-md shadow-elevated">
                <div className="flex items-center gap-3">
                  <BadgeCheck className="text-mint-deep shrink-0" size={26} aria-hidden="true" />
                  <div>
                    <div className="font-semibold text-brand-deep text-[14px]">Doctoras certificadas</div>
                    <div className="text-[12px] text-text-mute">Cédulas verificables</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Value props — lista numerada editorial (reemplaza grid de cards idénticas) */}
        <ol className="mt-24 grid gap-8 md:grid-cols-3">
          {VALUE_PROPS.map((item, i) => (
            <li key={i} className="grid grid-cols-[auto_1fr] gap-4 items-start">
              <span className="font-bold text-[2.6rem] leading-none text-brand/35 tabular-nums tracking-tight">
                {String(i + 1).padStart(2, '0')}
              </span>
              <div>
                <h3 className="text-[1.1rem] font-semibold text-brand-deep tracking-[-0.015em]">{item.title}</h3>
                <p className="text-text-body text-[14px] mt-1.5 leading-[1.55]">{item.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-16" />

        <PricingSection onSelectMembership={onSelectMembership} onStartBooking={onStartBooking} />
        {/* <QuickBooking /> */}
      </div>

      {/* Footer */}
      <footer className="bg-cool-canvas border-t border-brand-100 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={florLogo} alt="Daisies" className="h-8 w-auto" />
              <img src={letrasLogo} alt="Daisies" className="h-6 w-auto" />
            </div>
            <div className="flex items-center gap-6 text-sm text-brand-deep/70">
              <a href="https://app.daisies.mx" target="_blank" rel="noopener noreferrer" className="hover:text-brand transition-colors">App</a>
              <a href="mailto:contacto@daisies.mx" className="hover:text-brand transition-colors">Contacto</a>
            </div>
          </div>
          <div className="border-t border-brand-100 mt-6 pt-4 text-center text-xs text-brand-deep/60">
            © {new Date().getFullYear()} Daisies. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </>
  );
};

export default HeroSection;
